import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField, Button,
  Stack, IconButton, CircularProgress, Divider, InputAdornment, Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import { sendCandidateOnboarding, previewCandidateLetter, GET_HR_CONTACTS } from '../../api';

const ACCENT = '#0e7c86';
const ACCENT_DARK = '#0a5a62';

// Sends the onboarding pack to a selected candidate. HR sets the offer terms
// (designation / annual salary / date of joining) and can preview the Offer
// Letter, Appointment Letter and email before sending.
export default function SendOnboardingDialog({ applicant, onClose, onSent }) {
  const open = !!applicant;
  const [designation, setDesignation] = useState('');
  const [location, setLocation] = useState('');
  const [salary, setSalary] = useState('');
  const [doj, setDoj] = useState('');
  const [previewBusy, setPreviewBusy] = useState(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [hrContacts, setHrContacts] = useState([]);
  const [ccSelected, setCcSelected] = useState([]);

  useEffect(() => {
    if (applicant) {
      setDesignation(applicant.role || '');
      setLocation(''); setSalary(''); setDoj(''); setErr(null);
      setCcSelected([]);
    }
  }, [applicant]);

  // All HR mailboxes (HRContact) for the CC selector.
  useEffect(() => {
    let active = true;
    GET_HR_CONTACTS()
      .then((data) => { if (active) setHrContacts(Array.isArray(data) ? data.filter((c) => c && c.email) : []); })
      .catch(() => { /* CC list is optional — ignore load failures */ });
    return () => { active = false; };
  }, []);

  const terms = () => ({
    designation: designation || undefined,
    location: location || undefined,
    annual_salary: salary || undefined,
    date_of_joining: doj || undefined,
  });

  const openPreview = async (doc) => {
    setPreviewBusy(doc); setErr(null);
    try {
      const blob = await previewCandidateLetter(applicant.id, { doc, ...terms() });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setErr('Could not load the preview.');
    } finally {
      setPreviewBusy(null);
    }
  };

  const send = async () => {
    setSending(true); setErr(null);
    try {
      const cc = ccSelected.map((c) => c.email);
      const res = await sendCandidateOnboarding(applicant.id, { ...terms(), cc: cc.length ? cc : undefined });
      onSent(applicant, res);
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not send onboarding.');
    } finally {
      setSending(false);
    }
  };

  const previewBtn = (doc, label, icon) => (
    <Button size="small" variant="outlined" startIcon={previewBusy === doc ? <CircularProgress size={15} /> : icon}
      onClick={() => openPreview(doc)} disabled={!!previewBusy}
      sx={{ textTransform: 'none', borderColor: '#d5e0e4', color: ACCENT_DARK, borderRadius: '10px' }}>
      {label}
    </Button>
  );

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Send onboarding{applicant ? ` — ${applicant.name}` : ''}</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#7b96a2' }}>{applicant?.email_address}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={sending}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontWeight: 700, color: '#14232b', fontSize: 13.5, mb: 1 }}>Offer terms</Typography>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="Designation" value={designation} onChange={(e) => setDesignation(e.target.value)} fullWidth />
            <TextField size="small" label="Location" value={location} onChange={(e) => setLocation(e.target.value)} fullWidth placeholder="e.g. Bengaluru" />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="Annual salary" value={salary} onChange={(e) => setSalary(e.target.value.replace(/[^\d]/g, ''))}
              fullWidth InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} placeholder="e.g. 240000" />
            <TextField size="small" label="Date of joining" type="date" value={doj} onChange={(e) => setDoj(e.target.value)}
              fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: '#8a9099', mb: 2 }}>
          These are baked into the Offer &amp; Appointment letters. Leave blank to show a placeholder the candidate fills on joining.
        </Typography>

        <Typography sx={{ fontWeight: 700, color: '#14232b', fontSize: 13.5, mb: 1 }}>CC (HR)</Typography>
        <Autocomplete
          multiple
          size="small"
          options={hrContacts}
          value={ccSelected}
          onChange={(e, val) => setCcSelected(val)}
          getOptionLabel={(o) => o?.email || ''}
          isOptionEqualToValue={(o, v) => o.email === v.email}
          renderOption={(props, o) => (
            <li {...props} key={o.email}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#14232b' }}>{o.name || o.email}</Typography>
                {o.name && <Typography sx={{ fontSize: 11.5, color: '#7b96a2' }}>{o.email}</Typography>}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder={ccSelected.length ? '' : 'Select HR mailboxes to CC…'} />
          )}
          sx={{ mb: 0.5 }}
        />
        <Typography sx={{ fontSize: 11.5, color: '#8a9099', mb: 2 }}>
          Any HR mailbox you pick is CC&apos;d on the onboarding email.
        </Typography>

        <Divider sx={{ mb: 2 }} />
        <Typography sx={{ fontWeight: 700, color: '#14232b', fontSize: 13.5, mb: 1 }}>Preview before sending</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {previewBtn('offer_letter', 'Offer Letter', <DescriptionIcon sx={{ fontSize: 18 }} />)}
          {previewBtn('appointment_letter', 'Appointment Letter', <DescriptionIcon sx={{ fontSize: 18 }} />)}
          {previewBtn('email', 'Email', <EmailIcon sx={{ fontSize: 18 }} />)}
        </Stack>

        {err && <Typography sx={{ color: '#c0392b', fontSize: 13, mt: 2 }}>{err}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none', color: '#64757d' }}>Cancel</Button>
        <Button variant="contained" onClick={send} disabled={sending}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, px: 2.5, borderRadius: '10px' }}>
          Send onboarding
        </Button>
      </DialogActions>
    </Dialog>
  );
}
